import { Controller } from 'react-hook-form';
import { PieChartIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  FormSectionV2,
  FormSubsectionV2,
  PersonaCreatableSelectField,
  PersonaTagField,
  PersonaTextareaField,
} from '../../PersonaFormSectionsV2';
import { isFieldRequired } from '../CreatePersonaForm.schema';
import { useCreatePersonaFormContext } from '../hooks/useCreatePersonaFormContext';

export function SegmentDetailsSectionV2() {
  const { t } = useTranslation('audiences');
  const { control, config, getValues, setValue } = useCreatePersonaFormContext();

  return (
    <FormSectionV2
      icon={<PieChartIcon className="size-5" />}
      title={t('audiences.segmentDetailsSection.segmentDetails')}
      description={t('audiences.segmentDetailsSection.theSegmentWhichThisPersona')}
    >
      <Controller
        name="segmentName"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <PersonaCreatableSelectField
            {...field}
            onChange={(segmentName) => {
              const previousSeed = (
                config.segmentDescriptionByName[getValues('segmentName')] ?? ''
              ).trim();
              const current = getValues('segmentDescription').trim();
              field.onChange(segmentName);
              // The description follows the segment while it is still the one
              // seeded from the previous segment (or empty). Once the person has
              // edited it, it is theirs and a new segment must not overwrite it.
              if (current && current !== previousSeed) return;
              const next = config.segmentDescriptionByName[segmentName] ?? '';
              if (next !== current) {
                setValue('segmentDescription', next, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }
            }}
            label={t('audiences.segmentDetailsSection.segment')}
            placeholder={t('audiences.segmentDetailsSection.typeToAddANew')}
            options={config.segmentNameOptions}
            required={isFieldRequired(field.name)}
            error={error?.message}
          />
        )}
      />
      <Controller
        name="segmentDescription"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <PersonaTextareaField
            {...field}
            label={t('audiences.segmentDetailsSection.segmentDescription')}
            placeholder={t('audiences.segmentDetailsSection.addADetailedDescriptionOf')}
            rows={4}
            required={isFieldRequired(field.name)}
            error={error?.message}
          />
        )}
      />

      <FormSubsectionV2>{t('audiences.segmentDetailsSection.interests')}</FormSubsectionV2>

      <Controller
        name="topicsOfInterest"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <PersonaTagField
            {...field}
            label={t('audiences.segmentDetailsSection.topicsOfInterest')}
            placeholder={t('audiences.segmentDetailsSection.selectInterestsOrTypeAnd')}
            options={config.topicsOfInterestOptions}
            required={isFieldRequired(field.name)}
            error={error?.message}
          />
        )}
      />
    </FormSectionV2>
  );
}

SegmentDetailsSectionV2.displayName = 'SegmentDetailsSectionV2';
